import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import { useMobileMediaQuery, useTabletMediaQuery } from '@/shared/lib/hooks';
import { Card } from '@/shared/ui/card';
import { type OutcomeData, PatternOutcomeDoughnut } from '@/shared/ui/patterns';

import { usePatternLabMode } from '../../model/selectors/pattern-lab-mode';
import type { PatternStudy } from '../../model/types/pattern-study';
import { PatternLabTitle } from '../pattern-lab-title/pattern-lab-title';

import cls from './pattern-lab-outcome.module.css';

type PatternStudyOutcomeProps = {
    patterns: PatternStudy[];
};

export const PatternLabOutcome = memo(
    ({ patterns }: PatternStudyOutcomeProps) => {
        const { t } = useTranslation('pattern-studio');
        const isMobile = useMobileMediaQuery();
        const isTablet = useTabletMediaQuery();
        const mode = usePatternLabMode();

        const renderComparisonMode = () => (
            <div className={cls.root}>
                {patterns.map((patternStudy) => {
                    const outcomes: OutcomeData[] = [
                        {
                            type: 'Bullish',
                            count:
                                patternStudy.pattern.price_above_t_post_sum ??
                                0,
                            percentage: patternStudy.pattern.above_t_perc ?? 0,
                            variant: 'green',
                        },
                        {
                            type: 'Bearish',
                            count:
                                patternStudy.pattern.price_below_t_post_sum ??
                                0,
                            percentage: patternStudy.pattern.below_t_perc ?? 0,
                            variant: 'red',
                        },
                        {
                            type: 'Neutral',
                            count:
                                patternStudy.pattern.price_between_t_post_sum ??
                                0,
                            percentage:
                                patternStudy.pattern.between_t_perc ?? 0,
                            variant: 'blue',
                        },
                    ];

                    const centerCount =
                        patternStudy.pattern?.pattern_index_count?.toString() ??
                        '';

                    // Determine doughnut layout without nested ternaries
                    let doughnutLayout: 'vertical' | 'horizontal';

                    if (isMobile) {
                        doughnutLayout = 'vertical';
                    } else if (
                        (patterns.length > 1 && !isTablet) ||
                        mode === 'aggregation'
                    ) {
                        doughnutLayout = 'horizontal';
                    } else {
                        doughnutLayout = 'vertical';
                    }

                    return (
                        <div
                            key={patternStudy.pattern.unique_id}
                            className={cn({
                                [cls.multiple]: patterns.length > 1,
                                [cls.single]: patterns.length === 1,
                            })}
                        >
                            <Card
                                accentColor={patternStudy.color}
                                hasShadow={false}
                                hasSnowflake={false}
                            >
                                <PatternLabTitle
                                    title={
                                        patternStudy.name ??
                                        patternStudy.pattern.unique_id
                                    }
                                />

                                <PatternOutcomeDoughnut
                                    centerCount={centerCount}
                                    layout={doughnutLayout}
                                    outcomes={outcomes}
                                />
                            </Card>
                        </div>
                    );
                })}
            </div>
        );

        const renderAggregationMode = () => {
            // Calculate aggregated values
            let totalBullishCount = 0;
            let totalBearishCount = 0;
            let totalNeutralCount = 0;
            let totalPatternCount = 0;

            // Collect pattern IDs for display
            const patternIds: string[] = [];

            patterns.forEach((patternStudy) => {
                totalBullishCount +=
                    patternStudy.pattern.price_above_t_post_sum ?? 0;
                totalBearishCount +=
                    patternStudy.pattern.price_below_t_post_sum ?? 0;
                totalNeutralCount +=
                    patternStudy.pattern.price_between_t_post_sum ?? 0;
                totalPatternCount +=
                    patternStudy.pattern.pattern_index_count ?? 0;

                // Add pattern ID or name to the list
                patternIds.push(
                    patternStudy.name ?? patternStudy.pattern.unique_id,
                );
            });

            const totalCount =
                totalBullishCount + totalBearishCount + totalNeutralCount;

            // Calculate new percentages with 2 decimal places
            const bullishPercentage =
                totalCount > 0
                    ? Number(
                          ((totalBullishCount / totalCount) * 100).toFixed(2),
                      )
                    : 0;
            const bearishPercentage =
                totalCount > 0
                    ? Number(
                          ((totalBearishCount / totalCount) * 100).toFixed(2),
                      )
                    : 0;
            const neutralPercentage =
                totalCount > 0
                    ? Number(
                          ((totalNeutralCount / totalCount) * 100).toFixed(2),
                      )
                    : 0;

            const outcomes: OutcomeData[] = [
                {
                    type: 'Bullish',
                    count: totalBullishCount,
                    percentage: bullishPercentage,
                    variant: 'green',
                },
                {
                    type: 'Bearish',
                    count: totalBearishCount,
                    percentage: bearishPercentage,
                    variant: 'red',
                },
                {
                    type: 'Neutral',
                    count: totalNeutralCount,
                    percentage: neutralPercentage,
                    variant: 'blue',
                },
            ];

            return (
                <div className={cls.root}>
                    <div className={cls.single}>
                        <Card
                            hasShadow={false}
                            hasSnowflake={false}
                        >
                            <PatternLabTitle title={patternIds.join(', ')} />

                            <PatternOutcomeDoughnut
                                centerCount={totalPatternCount.toString()}
                                className={cls.largeDonut}
                                isAggregationMode
                                layout={isMobile ? 'vertical' : 'horizontal'}
                                outcomes={outcomes}
                            />
                        </Card>
                    </div>
                </div>
            );
        };

        return (
            <Card
                data-tour="pattern-lab-outcome"
                helpId="PATTERN_LIVE_OUTCOME_TYPES"
                isCollapsible
                isFollowingCursor={false}
                isWithoutPadding
                title={t('patternStats.patternOutcomeTypes')}
            >
                {mode === 'comparison'
                    ? renderComparisonMode()
                    : renderAggregationMode()}
            </Card>
        );
    },
);

PatternLabOutcome.displayName = 'PatternStudyOutcome';
